import classNames from 'classnames';
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ReactSVG } from 'react-svg';

import { useTranslation } from 'utils/i18n';
import useOutsideClick from '../../hooks/useOutsideClick';
import Badge from '../badge';

interface Props {
  label: string;
  compressLabel?: boolean;
  name?: string;
  // Numeric filter values and unset (null) slots are filtered out on use.
  selectedItems?: Array<string | number | null>;
  clearAllText?: string;
  children?: ReactNode;
  canApply?: boolean;
  applyOnClickOutside?: boolean;
  handleApply?: () => void;
  handleClearAll?: () => void;
  expandable?: boolean;
  displayButtons?: boolean;
  showDropdownArrow?: boolean;
  alwaysShowBadgeCount?: boolean;
  positionRelative?: boolean;
  hideApplyButton?: boolean;
  testId?: string;
  selectAll?: () => void;
  allSelected?: boolean;
  isEmptyFilters?: boolean;
  hideCancelButton?: boolean;
  hideSelectAllButton?: boolean;
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
}

const FilterDropdown: FC<Props> = ({
  label,
  compressLabel = true,
  name,
  selectedItems = [],
  clearAllText = 'Clear',
  children = null,
  canApply = true,
  applyOnClickOutside = false,
  handleApply = () => {},
  handleClearAll = () => {},
  expandable = false,
  displayButtons = true,
  showDropdownArrow = true,
  alwaysShowBadgeCount = false,
  positionRelative = false,
  hideApplyButton = false,
  testId,
  selectAll = () => {},
  allSelected = false,
  isEmptyFilters,
  hideCancelButton = false,
  hideSelectAllButton = true,
  isOpen: isOpenProp = false,
  setIsOpen: setIsOpenProp,
}) => {
  const { t } = useTranslation('common');
  const [internalIsOpen, internalSetIsOpen] = useState(isOpenProp);
  const isOpen = setIsOpenProp ? isOpenProp : internalIsOpen;
  const setIsOpen = setIsOpenProp || internalSetIsOpen;
  const ref = useRef<HTMLDivElement>(null);

  const classes = classNames({
    field: true,
    'field--no-margin': true,
    'filter-dropdown': true,
    'filter-dropdown--open': !!isOpen && !!showDropdownArrow,
    'filter-dropdown--closed': !isOpen && !!showDropdownArrow,
    [`filter-dropdown--${name}`]: name,
  });

  const innerClasses = classNames({
    'filter-dropdown__inner': true,
    'filter-dropdown__inner--expandable': !!expandable,
    'filter-dropdown__inner--relative': !!positionRelative,
  });

  const buttonClasses = classNames({
    button: true,
    'button--block': true,
    'button--dropdown': !!showDropdownArrow,
    'button--dropdown-without-arrow': !showDropdownArrow,
  });

  const items = selectedItems.filter(item => item);

  const renderBadge = () => {
    if ((alwaysShowBadgeCount && items.length >= 1) || items.length > 1) {
      return <Badge count={items.length} />;
    }

    return null;
  };

  const apply = useCallback(() => {
    handleApply();
    setIsOpen(false);
  }, [handleApply, setIsOpen]);

  // Apply filter changes when 'enter' key is pressed
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.keyCode === 13) {
        apply();
      }
    },
    [apply]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, false);
    };
  }, [handleKeyDown]);

  useOutsideClick(ref, () => {
    setIsOpen(false);
  });

  useEffect(() => {
    if (applyOnClickOutside && !isOpen) {
      handleApply();
    }
  }, [applyOnClickOutside, handleApply, isOpen]);

  useEffect(() => {
    if (isOpen && expandable) {
      document.body.style.overflow = 'hidden';
    } else if (!isOpen && expandable) {
      document.body.style.overflow = 'visible';
    }
  });

  const clear = () => {
    handleClearAll();
    setIsOpen(false);
  };

  const handleClick = () => {
    if (isOpen) {
      setIsOpen(false);
    } else {
      setIsOpen(true);
    }
  };

  const selectButton = () => {
    if (hideSelectAllButton) {
      return null;
    }
    if (allSelected) {
      return (
        <button
          onClick={handleClearAll}
          className="button button--text right-align"
          data-cy="deselectAllFilters"
          data-testid="deselectAllFilters"
        >
          Deselect All
        </button>
      );
    }
    return (
      <button
        onClick={selectAll}
        className="button button--text right-align"
        data-cy="selectAllFilters"
        data-testid="selectAllFilters"
      >
        Select All
      </button>
    );
  };

  const dropdownLabel = () => {
    const labelLengthLimit = 10;

    if (selectedItems.length === 1) {
      const [selected] = selectedItems;
      const selectedLabel = selected == null ? '' : String(selected);
      if (compressLabel && selectedLabel.length > labelLengthLimit) {
        return `${selectedLabel.slice(0, labelLengthLimit)}...`;
      }
      return selected;
    }

    if (compressLabel && label.length > labelLengthLimit) {
      return `${label.slice(0, labelLengthLimit)}...`;
    }

    return label;
  };

  return (
    <div ref={ref} className="form__field" data-testid="filterDropdown">
      <div className={classes}>
        <button
          data-testid={testId}
          className={buttonClasses}
          onClick={handleClick}
          data-cy={testId}
        >
          {dropdownLabel()}
          {renderBadge()}
          {!showDropdownArrow && (
            <ReactSVG
              src="/images/icons/filters.svg"
              id="mobile-search-filters"
            />
          )}
        </button>
        {!!isOpen && (
          <div className={innerClasses} data-testid="filterOptions">
            {!isEmptyFilters && <div className="buttons">{selectButton()}</div>}
            {isEmptyFilters && <p>No options matched.</p>}
            {!isEmptyFilters && children}
            {!!displayButtons && (
              <div className="buttons">
                {!hideCancelButton && (
                  <button
                    onClick={clear}
                    className="button button--text"
                    data-cy="resetAllFilters"
                  >
                    {clearAllText}
                  </button>
                )}
                {!hideApplyButton && !isEmptyFilters && (
                  <button
                    onClick={apply}
                    className="button button--primary button--small right-align"
                    disabled={!canApply}
                    data-cy="apply"
                  >
                    {t('apply')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterDropdown;
